import React from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
} from '@mui/material';

/**
 * Shared dashboard layout with sidebar navigation (desktop) / tabs (mobile).
 *
 * Props:
 *   title       - string (dashboard heading)
 *   subtitle    - string
 *   sections    - Array<{ key: string, label: string, icon: ReactNode }>
 *   activeSection - string (current section key)
 *   onSectionChange - (key: string) => void
 *   children    - ReactNode (content for the active section)
 */
const DashboardLayout = ({ title, subtitle, sections, activeSection, onSectionChange, children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (isMobile) {
    // Horizontal tabs on mobile
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ px: 2, pt: 3, pb: 1 }}>
          <Typography variant="h4" gutterBottom>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Tabs
          value={activeSection}
          onChange={(_, val) => onSectionChange(val)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: '2px solid',
            borderColor: 'secondary.light',
            mb: 2,
            '& .MuiTab-root': {
              color: 'text.secondary',
              fontFamily: '"Space Mono", monospace',
              fontSize: '0.75rem',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              minHeight: 48,
            },
            '& .Mui-selected': {
              color: 'primary.main',
            },
            '& .MuiTabs-indicator': {
              bgcolor: 'primary.main',
            },
          }}
        >
          {sections.map((s) => (
            <Tab key={s.key} value={s.key} label={s.label} icon={s.icon} iconPosition="start" />
          ))}
        </Tabs>
        <Box sx={{ px: 2, pb: 4 }}>{children}</Box>
      </Box>
    );
  }

  // Sidebar layout on desktop
  return (
    <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 64px)' }}>
      {/* Sidebar */}
      <Box
        sx={{
          width: 240,
          flexShrink: 0,
          borderRight: '2px solid',
          borderColor: 'secondary.light',
          bgcolor: 'background.default',
          pt: 4,
          px: 1,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            px: 2,
            mb: 0.5,
            fontFamily: '"Bebas Neue", sans-serif',
            letterSpacing: '0.05em',
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            sx={{ px: 2, mb: 3, display: 'block', color: 'text.secondary' }}
          >
            {subtitle}
          </Typography>
        )}
        <List sx={{ mt: 2 }}>
          {sections.map((s) => (
            <ListItemButton
              key={s.key}
              selected={activeSection === s.key}
              onClick={() => onSectionChange(s.key)}
              sx={{
                borderRadius: 0,
                mb: 0.5,
                '&.Mui-selected': {
                  bgcolor: 'rgba(255, 107, 26, 0.1)',
                  borderLeft: '3px solid',
                  borderColor: 'primary.main',
                  '&:hover': {
                    bgcolor: 'rgba(255, 107, 26, 0.15)',
                  },
                },
                '&:hover': {
                  bgcolor: 'rgba(255, 107, 26, 0.05)',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{s.icon}</ListItemIcon>
              <ListItemText
                primary={s.label}
                primaryTypographyProps={{
                  fontSize: '0.85rem',
                  fontFamily: '"Space Mono", monospace',
                  letterSpacing: '0.03em',
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Box>

      {/* Main content */}
      <Box sx={{ flex: 1, p: 4, overflow: 'auto' }}>{children}</Box>
    </Box>
  );
};

export default DashboardLayout;
